import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Table, ArrowSquareOut, X, GitDiff } from "@phosphor-icons/react";
import BatchDiffView from "@/components/BatchDiffView";

/* ============================================================
   SAVED BATCHES PANEL · v2.79
   ------------------------------------------------------------
   Surfaces conductor-sizing batches saved under a project on the
   project's Sizing tab. Each row deep-links into the Batch tool
   with `?tab=batch&batch=<id>` — BatchConductor auto-loads.
   ============================================================ */
export default function SavedBatchesPanel({ projectId }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  // v2.80 · pick two batches to compare.
  const [pickedIds, setPickedIds] = useState([]);
  const [diffPair, setDiffPair] = useState(null); // { a, b } full batches
  const [comparing, setComparing] = useState(false);

  const refresh = async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/calculators/conductor-sizing/batches?project_id=${encodeURIComponent(projectId)}`,
      );
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { refresh(); }, [projectId]);

  const togglePick = (batchId, ev) => {
    ev.stopPropagation();
    setPickedIds((prev) => {
      if (prev.includes(batchId)) return prev.filter((x) => x !== batchId);
      // cap at 2 — drop the oldest pick if the user tries a 3rd.
      const next = [...prev, batchId];
      return next.length > 2 ? next.slice(-2) : next;
    });
    // Clear any previously-loaded diff whenever picks change.
    setDiffPair(null);
  };

  const compareTwo = async () => {
    if (pickedIds.length !== 2) return;
    setComparing(true);
    try {
      const [a, b] = await Promise.all(
        pickedIds.map((id) => api.get(`/calculators/conductor-sizing/batches/${id}`).then((r) => r.data)),
      );
      setDiffPair({ a, b });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Compare failed");
    } finally {
      setComparing(false);
    }
  };

  const openInBatchTool = (batchId) => {
    nav(`/tools?tab=batch&batch=${encodeURIComponent(batchId)}`);
  };

  const remove = async (batchId, ev) => {
    ev.stopPropagation();
    if (!window.confirm("Delete this saved batch? This cannot be undone.")) return;
    try {
      await api.delete(`/calculators/conductor-sizing/batches/${batchId}`);
      toast.success("Saved batch deleted");
      setPickedIds((prev) => prev.filter((x) => x !== batchId));
      if (diffPair && (diffPair.a?.id === batchId || diffPair.b?.id === batchId)) {
        setDiffPair(null);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="border border-white/10 bg-[#12151C] p-6" data-testid="saved-batches-panel">
      <div className="flex items-center gap-2 mb-1">
        <Table weight="fill" size={18} className="text-[#00E5FF]" />
        <div className="label-tag">// Saved sizing batches</div>
      </div>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold">Saved conductor batches</h3>
        <button
          onClick={() => nav("/tools?tab=batch")}
          className="border border-[#00E5FF]/40 text-[#00E5FF] px-3 py-1 text-xs font-mono hover:bg-[#00E5FF]/10 inline-flex items-center gap-1.5"
          data-testid="saved-batches-new">
          + New batch
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-1">
        Every batch you save from the Batch conductor tool with this project selected shows up here — click Open to jump straight back in with all rows and results restored.
      </p>

      <div className="mt-4">
        {busy && (
          <div className="text-xs font-mono text-gray-500" data-testid="saved-batches-loading">
            Loading…
          </div>
        )}
        {!busy && items.length === 0 && (
          <div className="border border-white/5 bg-black/40 p-4 text-xs font-mono text-gray-500 text-center"
            data-testid="saved-batches-empty">
            No saved batches yet. Head to <span className="text-[#00E5FF]">Tools → Batch conductor</span>, pick this project, and click <span className="text-[#00E5FF]">Save to project</span>.
          </div>
        )}
        {!busy && items.length > 0 && (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-[10px] font-mono text-gray-400"
              data-testid="saved-batches-compare-bar">
              <span>{pickedIds.length}/2 picked for compare</span>
              {pickedIds.length > 0 && (
                <button onClick={() => { setPickedIds([]); setDiffPair(null); }}
                  className="text-gray-500 hover:text-white underline text-[10px]"
                  data-testid="saved-batches-clear-picks">
                  clear
                </button>
              )}
              <button onClick={compareTwo}
                disabled={pickedIds.length !== 2 || comparing}
                className="ml-auto border border-[#FFCC00]/40 text-[#FFCC00] px-3 py-1 text-[11px] font-mono hover:bg-[#FFCC00]/10 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                data-testid="saved-batches-compare">
                <GitDiff size={12} /> {comparing ? "Loading…" : "Compare selected (2)"}
              </button>
            </div>
            <div className="border border-white/10 divide-y divide-white/5" data-testid="saved-batches-list">
              {items.map((b) => {
                const isPicked = pickedIds.includes(b.id);
                return (
                  <div key={b.id}
                    onClick={() => openInBatchTool(b.id)}
                    className={`flex items-center gap-3 px-3 py-2 hover:bg-white/[0.04] text-xs font-mono cursor-pointer ${isPicked ? "bg-[#FFCC00]/[0.08]" : ""}`}
                    data-testid={`saved-batch-${b.id}`}>
                    <input type="checkbox"
                      checked={isPicked}
                      onChange={(ev) => togglePick(b.id, ev)}
                      onClick={(ev) => ev.stopPropagation()}
                      data-testid={`saved-batch-${b.id}-pick`}
                      title="Pick for compare (max 2)" />
                    <div className="flex-1 min-w-0">
                      <div className="text-white truncate">{b.project_name || "—"}</div>
                      <div className="text-[10px] text-gray-500">
                        {b.row_count} row{b.row_count !== 1 ? "s" : ""}
                        {b.result_count > 0 && <span className="text-[#00E5FF]"> · {b.result_count} computed</span>}
                        {b.engineer_name && <span> · {b.engineer_name}</span>}
                        {b.engineer_pe_license && <span> · PE {b.engineer_pe_license}</span>}
                        <span> · {new Date(b.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <button
                      onClick={(ev) => { ev.stopPropagation(); openInBatchTool(b.id); }}
                      className="border border-[#00E5FF]/40 text-[#00E5FF] px-2 py-1 text-[11px] hover:bg-[#00E5FF]/10 inline-flex items-center gap-1"
                      data-testid={`saved-batch-${b.id}-open`}>
                      <ArrowSquareOut size={12} /> Open
                    </button>
                    <button
                      onClick={(ev) => remove(b.id, ev)}
                      className="text-gray-500 hover:text-[#FF3B30] px-1"
                      data-testid={`saved-batch-${b.id}-delete`}
                      title="Delete this saved batch">
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {diffPair && (
        <BatchDiffView batchA={diffPair.a} batchB={diffPair.b}
          onClose={() => setDiffPair(null)} />
      )}
    </div>
  );
}
